import { useEffect, useRef, useState } from "react";

/**
 * Tiny wind chip for the Garmin-style watch face.
 *
 * Shows: a rotated arrow (head/tail/cross direction relative to the shot
 * line player→pin) + the wind speed in mph. Colour-codes the chip by
 * relative direction:
 *   • Headwind  → terracotta red  (club up)
 *   • Tailwind  → green           (club down)
 *   • Crosswind → amber           (aim adjust)
 *
 * Free data source: open-meteo.com (no API key, CORS enabled).
 * Refreshes every 15 minutes; re-fetches only when the player has moved
 * >250m to keep watch bandwidth tight.
 */
const REFRESH_MS = 15 * 60 * 1000;
const MIN_DIST_TO_REFETCH_M = 250;

function bearingDeg(lat1, lng1, lat2, lng2) {
  const toRad = (d) => (d * Math.PI) / 180;
  const toDeg = (r) => (r * 180) / Math.PI;
  const dLng = toRad(lng2 - lng1);
  const y = Math.sin(dLng) * Math.cos(toRad(lat2));
  const x =
    Math.cos(toRad(lat1)) * Math.sin(toRad(lat2)) -
    Math.sin(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.cos(dLng);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
}

function metersBetween(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function classifyWind(windFromDeg, shotBearing) {
  if (windFromDeg == null || shotBearing == null) return "cross";
  const rel = ((windFromDeg - shotBearing + 540) % 360) - 180;
  const abs = Math.abs(rel);
  if (abs <= 30) return "head";
  if (abs >= 150) return "tail";
  return "cross";
}

export default function WatchWindChip({ playerLat, playerLng, pinLat, pinLng, onWind, inline = false }) {
  const [w, setW] = useState(null);
  const lastFetchRef = useRef({ lat: null, lng: null, at: 0 });

  useEffect(() => {
    if (playerLat == null || playerLng == null) return;
    const last = lastFetchRef.current;
    const stale = Date.now() - last.at > REFRESH_MS;
    const moved =
      last.lat == null ||
      metersBetween(last.lat, last.lng, playerLat, playerLng) > MIN_DIST_TO_REFETCH_M;
    if (!w && !stale && !moved) return;
    if (!stale && !moved) return;

    let cancelled = false;
    const fetchIt = async () => {
      try {
        const ctrl = new AbortController();
        const t = setTimeout(() => ctrl.abort(), 8000);
        const url = `https://api.open-meteo.com/v1/forecast?latitude=${playerLat}&longitude=${playerLng}&current=wind_speed_10m,wind_direction_10m&wind_speed_unit=mph&timezone=auto`;
        const res = await fetch(url, { signal: ctrl.signal });
        clearTimeout(t);
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        const c = data?.current || {};
        setW({
          mph: typeof c.wind_speed_10m === "number" ? Math.round(c.wind_speed_10m) : null,
          fromDeg: typeof c.wind_direction_10m === "number" ? c.wind_direction_10m : null,
        });
        lastFetchRef.current = { lat: playerLat, lng: playerLng, at: Date.now() };
      } catch {
        /* silent — chip just won't show */
      }
    };
    fetchIt();
    const id = setInterval(fetchIt, REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [playerLat, playerLng]);

  if (!w || w.mph == null || w.fromDeg == null) {
    return <WindBubble onWind={onWind} payload={null} />;
  }
  // Calm — don't waste pixels showing 0-1mph readings on a watch face
  if (w.mph < 3) {
    return <WindBubble onWind={onWind} payload={null} />;
  }

  const shotBearing =
    pinLat != null && pinLng != null && playerLat != null && playerLng != null
      ? bearingDeg(playerLat, playerLng, pinLat, pinLng)
      : null;
  const cls = classifyWind(w.fromDeg, shotBearing);
  // Bubble wind state up so the parent can compute wind-adjusted yardages.
  // Tour rule of thumb: ≈1yd adjustment per 1mph for irons; crosswind
  // doesn't materially change carry distance so delta=0.
  const windPayload = { mph: w.mph, cls, deltaYds: cls === "head" ? +w.mph : cls === "tail" ? -w.mph : 0 };
  // Arrow points in the direction the wind is GOING (opposite of `fromDeg`)
  // but rotated into the player's local frame so "up" = ahead of the shot.
  // i.e. arrow heading = (wind_to_deg - shotBearing) — head/tail arrow is
  // self-explanatory ↓/↑ relative to the player's facing direction.
  const arrowAngle =
    shotBearing != null
      ? (((w.fromDeg + 180) - shotBearing) + 360) % 360
      : 0;

  const palette = {
    head: { bg: "rgba(239,68,68,0.18)", border: "rgba(239,68,68,0.55)", fg: "#fca5a5" },
    tail: { bg: "rgba(34,197,94,0.18)", border: "rgba(34,197,94,0.55)", fg: "#86efac" },
    cross: { bg: "rgba(251,191,36,0.18)", border: "rgba(251,191,36,0.55)", fg: "#fcd34d" },
  }[cls];

  return (
    <>
      <WindBubble onWind={onWind} payload={windPayload} />
      <div
        data-testid="watch-wind-chip"
      title={`${w.mph}mph ${cls === "head" ? "headwind" : cls === "tail" ? "tailwind" : "crosswind"}`}
      style={{
        position: inline ? "static" : "absolute",
        ...(inline ? {} : { top: "30%", left: "12%" }),
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        background: palette.bg,
        color: palette.fg,
        border: `1px solid ${palette.border}`,
        borderRadius: 9999,
        padding: "2px 9px 2px 7px",
        fontSize: "clamp(11px, 3vmin, 18px)",
        fontWeight: 800,
        letterSpacing: 0.3,
        fontVariantNumeric: "tabular-nums",
        pointerEvents: "none",
        backdropFilter: "blur(6px)",
      }}
    >
      <svg
        width="14" height="14" viewBox="0 0 10 10"
        style={{ transform: `rotate(${arrowAngle}deg)`, transformOrigin: "center", width: "clamp(14px, 3.4vmin, 22px)", height: "clamp(14px, 3.4vmin, 22px)" }}
        aria-hidden="true"
      >
        <path d="M5 0.5 L8 6 L5.6 6 L5.6 9.5 L4.4 9.5 L4.4 6 L2 6 Z" fill={palette.fg} />
      </svg>
      <span>{w.mph}</span>
      <span style={{ fontSize: "clamp(10px, 2.2vmin, 14px)", opacity: 0.7, marginLeft: 2 }}>MPH</span>
      </div>
    </>
  );
}

/** Side-effect-only child that bubbles wind payload up via useEffect, so we
    never call setState during a parent render (which React warns about). */
function WindBubble({ onWind, payload }) {
  useEffect(() => {
    if (onWind) onWind(payload);
  }, [onWind, payload?.mph, payload?.cls, payload?.deltaYds]);
  return null;
}
